import isFunction from 'lodash/isFunction';

import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import moment from 'moment-timezone';

import { Link } from 'react-router-dom';

import Admin from 'hive-admin/src/components/Admin';
import Query from 'hive-admin/src/components/Query';
import FieldReact from 'hive-admin/src/components/FieldReact';

import AntdTable from 'antd/lib/table';
import AntdList from 'antd/lib/list';
import AntdTimeline from 'antd/lib/timeline';
// import {
//   UpOutlined,
//   DownOutlined,
// } from '@ant-design/icons';

import Types from '../modules/types';

import tests from '../helpers/tests';

const Table = styled(AntdTable)`
  .ant-table-body {
    margin: 0px !important;
  }
`;

const Timeline = styled(AntdTimeline)`
  .ant-timeline-item-content {
    > p:first-child {
      margin-bottom: 0em;
    }
  }
`;

const List = styled(AntdList)`
  code {
    font-size: 85%;
  }
  &.ant-list-sm .ant-list-item {
    padding-top: 12px;
    padding-bottom: 12px;
  }
`;

function renderTextDiff(lines) {
  return (
    <ul className="jsondiffpatch-textdiff">
      {
        lines.map(line => (
          <li key={`${line.location.line}:${line.location.chr}`}>
            <div className="jsondiffpatch-textdiff-location">
              <span className="jsondiffpatch-textdiff-line-number">
                {line.location.line}
              </span>
              {/* <span class="jsondiffpatch-textdiff-char">
                {line.location.chr}
              </span> */}
            </div>
            <div className="jsondiffpatch-textdiff-line">
              {
                (line.pieces || []).map(piece => (
                  <span
                    key={piece.text}
                    className={`jsondiffpatch-textdiff-${piece.type}`}
                    data-text={
                        piece.text === '\n'
                      ? '@@NEWLINE@@'
                      : piece.text
                    }
                  >
                    {piece.text}
                  </span>
                ))
              }
            </div>
          </li>
        ))
      }
    </ul>
  )
}

function parseTextDiff(value) {
  const output = [];
  const lines = value.split('\n@@ ');
  for (let i = 0, l = lines.length; i < l; i++) {
    const line = lines[i];
    const lineOutput = {
      pieces: [],
    };
    const location = /^(?:@@ )?[-+]?(\d+),(\d+)/.exec(line).slice(1);
    lineOutput.location = {
      line: location[0],
      chr: location[1],
    };
    const pieces = line.split('\n').slice(1);
    for (
      let pieceIndex = 0, piecesLength = pieces.length;
      pieceIndex < piecesLength;
      pieceIndex++
    ) {
      const piece = pieces[pieceIndex];
      if (!piece.length) {
        // eslint-disable-next-line no-continue
        continue;
      }
      const pieceOutput = {
        type: 'context',
      };
      const pieceTypeChar = piece.substring(0, 1);
      if (pieceTypeChar === '+') {
        pieceOutput.type = 'added';
      } else if (pieceTypeChar === '-') {
        pieceOutput.type = 'deleted';
      }
      pieceOutput.text = decodeURI(piece.slice(1));
      lineOutput.pieces.push(pieceOutput);
    }
    output.push(lineOutput);
  }
  return output;
}

function parseDiff(target, result = [], keys = []) {
  if (!target) {
    return undefined;
  }
  const targetIsArray = target._t === 'a';
  let targetKeys = Object.keys(target);
  if (targetIsArray) {
    targetKeys = (
      targetKeys
      .map((key) => ([
        key,
        (
            key === '_t'
          ? -1
          : /^_[0-9]+$/.test(key)
          ? (parseInt(key.slice(1), 10))
          : (parseInt(key, 10) + 0.1)
        ),
      ]))
      .sort((a, b) => a[1] - b[1])
      .map(([key]) => key)
    );
  }
  targetKeys.forEach((key) => {
    if (key !== '_t') {
      const value = target[key];
      if (Array.isArray(value)) {
        const item = {
          raw: value,
        };
        if (targetIsArray) {
          item.array = true;
        }
        if (value[2] === 3) {
          // Move (array)
          const moveFromIndex = parseInt(key.slice(1), 10);
          const moveToIndex = parseInt(value[1], 10);
          Object.assign(item, {
            path: [...keys, moveFromIndex].join('.'),
            type: 'move',
            label: 'Move',
            moveFromIndex,
            moveToIndex,
          });
          result.push(item);
        } else if (value[2] === 0) {
          // Delete
          Object.assign(item, {
            path: [
              ...keys,
              (item.array ? key.slice(1) : key),
            ].join('.'),
            deleteValue: value[0],
            type: 'delete',
            label: 'Remove',
          });
          if (item.array) {
            item.deleteIndex = parseInt(key.slice(1), 10);
          }
          result.push(item);
        } else if (value[2] === 2) {
          if (value[0]?.length) {
            // Text diff (modify)
            Object.assign(item, {
              path: [
                ...keys,
                (item.array ? key.slice(1) : key),
              ].join('.'),
              type: 'text',
              label: 'Text',
              diff: parseTextDiff(value[0]),
            });
            result.push(item);
          }
        } else if (value.length === 2) {
          // Set (modify)
          Object.assign(item, {
            path: [...keys, key].join('.'),
            type: 'set',
            label: 'Update',
            setFrom: value[0],
            setTo: value[1],
          });
          result.push(item);
        } else if (value.length === 1) {
          // Set (add)
          Object.assign(item, {
            path: [...keys, key].join('.'),
            type: 'add',
            label: 'Add',
            addValue: value[0],
          });
          if (item.array) {
            item.addIndex = parseInt(key, 10);
          }
          result.push(item);
        }
      } else {
        parseDiff(value, result, [...keys, key]);
      }
    }
  });
  return result;
}

function toJsonWithMaxLength(value, maxLength = Infinity, block = false) {
  const string = (
    block
    ? JSON.stringify(value, null, '  ')
    : JSON.stringify(value)
  );
  if (string.length >= maxLength) {
    return `${string.slice(0, maxLength)}...`;
  }
  return string;
}

function InlineCodeBlock({ children, json, html, maxLength = 50 }) {
  return (
    <strong>
      <code>
        {
            html // eslint-disable-next-line react/no-danger
          ? <div dangerouslySetInnerHTML={{ __html: children }} />
          : json
          ? toJsonWithMaxLength(children, maxLength, true)
          : children
        }
      </code>
    </strong>
  );
}

function CodeBlock({ children, json, html, maxLength }) {
  return (
    <pre>
      <strong>
        <code>
          {
              html // eslint-disable-next-line react/no-danger
            ? <div dangerouslySetInnerHTML={{ __html: children }} />
            : json
            ? toJsonWithMaxLength(children, maxLength, true)
            : children
          }
        </code>
      </strong>
    </pre>
  );
}

const TextDiffBlockWrapper = styled.div`
  .jsondiffpatch-textdiff {
    padding-left: 6px;
    > li {
      &:before {
        content: "-";
        display: inline-block;
      }
      display: flex;
      .jsondiffpatch-textdiff-location {
        display: none;
        min-width: 40px;
        text-align: right;
        .jsondiffpatch-textdiff-line-number {
          &:after {
            content: ":";
            display: inline;
          }
        }
      }
      .jsondiffpatch-textdiff-line {
        padding-left: 10px;
        width: 100%;
        .jsondiffpatch-textdiff-context {
          &:first-child:before {
            content: "...";
            display: inline;
          }
          &:last-child:after {
            content: "...";
            display: inline;
          }
        }
        .jsondiffpatch-textdiff-added,
        .jsondiffpatch-textdiff-deleted {
          &[data-text="@@NEWLINE@@"] {
            &:before {
              content: " ";
              display: inline-block;
            }
          }
        }
        .jsondiffpatch-textdiff-added {
          background: rgba(0, 255, 0, 0.3);
        }
        .jsondiffpatch-textdiff-deleted {
          background: rgba(255, 0, 0, 0.3);
        }
      }
    }
  }
`;

function TextDiffBlock({ children }) {
  return (
    <pre>
      <strong>
        <code>
          <TextDiffBlockWrapper>
            {children}
          </TextDiffBlockWrapper>
        </code>
      </strong>
    </pre>
  );
}

const PayloadDiffItemDescriptionWrapper = styled.div`
  padding-left: 10px;
  padding-right: 10px;
  width: 100%;
  display: flex;
  gap: 15px;
`;

const PayloadDiffItemDescriptionWrapperBlock = styled.div`
  /* display: flex;
  flex-direction: column; */
  width: 100%;
  p, pre { margin: 0px }
  p { padding-bottom: 5px }
`;

function PayloadDiff({
  diff,
}) {
  return (
    <>
      {
        diff?.length
        ? (
            <List
              size="small"
              // header="Changes"
              header={null}
              dataSource={diff}
              renderItem={item => (
                <List.Item>
                  <List.Item.Meta
                    title={(
                      <span>
                        <strong>
                          {`${item.label}: `}
                        </strong>
                        <code>
                          {item.path}
                        </code>
                      </span>
                    )}
                    description={(
                      <PayloadDiffItemDescriptionWrapper>
                        {
                            item.type === 'move'
                          ? (
                              <>
                                <PayloadDiffItemDescriptionWrapperBlock>
                                  <p>From:</p>
                                  <CodeBlock json>
                                    {`Index: ${item.moveFromIndex}`}
                                  </CodeBlock>
                                </PayloadDiffItemDescriptionWrapperBlock>
                                <PayloadDiffItemDescriptionWrapperBlock>
                                  <p>To:</p>
                                  <CodeBlock json>
                                    {`Index: ${item.moveToIndex}`}
                                  </CodeBlock>
                                </PayloadDiffItemDescriptionWrapperBlock>
                              </>
                            )
                          : item.type === 'delete'
                          ? (
                              <PayloadDiffItemDescriptionWrapperBlock>
                                <p>
                                  {`Removed${
                                    item.array
                                    ? ` from index: ${item.deleteIndex}`
                                    : ':'
                                  }`}
                                </p>
                                <CodeBlock json>
                                  {item.deleteValue}
                                </CodeBlock>
                              </PayloadDiffItemDescriptionWrapperBlock>
                            )
                          : item.type === 'text'
                          ? (
                              <PayloadDiffItemDescriptionWrapperBlock>
                                <TextDiffBlock>
                                  {renderTextDiff(item.diff)}
                                </TextDiffBlock>
                              </PayloadDiffItemDescriptionWrapperBlock>
                            )
                          : item.type === 'set'
                          ? (
                              <>
                                <PayloadDiffItemDescriptionWrapperBlock>
                                  <p>From:</p>
                                  <CodeBlock json>
                                    {item.setFrom}
                                  </CodeBlock>
                                </PayloadDiffItemDescriptionWrapperBlock>
                                <PayloadDiffItemDescriptionWrapperBlock>
                                  <p>To:</p>
                                  <CodeBlock json>
                                    {item.setTo}
                                  </CodeBlock>
                                </PayloadDiffItemDescriptionWrapperBlock>
                              </>
                            )
                          : item.type === 'add'
                          ? (
                              <PayloadDiffItemDescriptionWrapperBlock>
                                <p>
                                  {`Added${
                                    item.array
                                    ? ` at index: ${item.addIndex}`
                                    : ':'
                                  }`}
                                </p>
                                <CodeBlock json>
                                  {item.addValue}
                                </CodeBlock>
                              </PayloadDiffItemDescriptionWrapperBlock>
                            )
                          : 'Modified:'
                        }
                      </PayloadDiffItemDescriptionWrapper>
                    )}
                  />
                </List.Item>
              )}
            />
          )
        : null
      }
    </>
  );
}

const PayloadDiffCached = React.memo((props) => {
  const diff = useMemo(
    () => parseDiff(props.diff),
    [props.diff],
  );
  return <PayloadDiff {...props} diff={diff} />;
});

const ChangeLogTableExpandWrapper = styled.div`
  padding: 10px;
  pre {
    padding: 5px 10px;
    border-radius: ${({ theme }) => theme.less.borderRadius};
    border: 1px solid #ddd;
    background: #f3f3f3;
    white-space: pre-wrap;
    font-size: 80%;
  }
`;

// eslint-disable-next-line import/prefer-default-export
export function ChangeLogTable({
  viewer,
  client,
  whereConditions,
  skipColumns,
  extractData,
}) {
  const [pageCurrent, setPageCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const pageSizeOptions = ['5', '10', '50'];
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const columns = useMemo(
    () => [{
      id: 'action',
      title: 'Action',
      dataIndex: 'action',
      render: (text, record) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {
            `${
              Types.CHANGE_LOG_ACTION_MAP[text]?.label
            }${
              record.steps?.length > 1
              ? ` (${record.steps.length})`
              : ''
            }`
          }
        </span>
      ),
    }, {
      id: 'realm',
      title: 'Realm',
      dataIndex: 'target.type',
    }, {
      id: 'target',
      title: 'Target',
      dataIndex: 'target.name',
      render: (text, record) => {
        const { links, getLabel } = (
          Types.CHANGE_LOG_TARGET_TYPE_MAP[record.target.type] || {}
        ) || {};
        const labelRaw = (
             record.target?.subject?.name
          || record.target?.name
          || '-'
        );
        const label = getLabel ? getLabel(labelRaw, record) : labelRaw;
        if (record?.target?.subject?._id) {
          let link = null;
          let linkIsFinal = false;
          const getLink = links?.[viewer?.role] || links?.['*'];
          if (getLink) {
            if (isFunction(getLink)) {
              link = getLink(record);
              linkIsFinal = true;
            } else {
              link = getLink;
              linkIsFinal = false;
            }
          }
          return (
            link
            ? (
                <Link
                  target="_blank"
                  to={
                    linkIsFinal
                    ? link
                    : `${link}/${
                        record.target.subject?._id || record.target.subject
                      }`
                  }
                >
                  {label}
                </Link>
              )
            : (
                <span style={{ opacity: 0.4 }}>
                  {label}
                </span>
              )
          );
        }
        return (
          <span style={{ opacity: 0.4 }}>
            {label}
          </span>
        );
      },
    }, {
      id: 'date',
      title: 'Date',
      dataIndex: 'updatedAt',
      render: (text, record) => (
        <span title={text}>
          {moment(record.updatedAt).calendar(null, {
            sameElse: 'lll',
          })}
        </span>
      ),
    }, {
      id: 'author',
      title: 'Author',
      dataIndex: 'author.name',
      render: (text, record) => (
        record?.author?.type === 'SYSTEM'
        ? 'System'
        : record?.author?.subject?._id
        ? (
            <Link
              target="_blank"
              to={`/users/${record?.author?.subject?._id}`}
            >
              {record?.author?.subject?.name || text || 'Unknown'}
            </Link>
          )
        : '-'
      ),
    // }, {
    //   id: 'snapshot',
    //   title: 'Restore',
    //   dataIndex: 'snapshot',
    //   render: (text, record) => (
    //     record.snapshot
    //     ? 'Yes'
    //     : '-'
    //   ),
    }].reduce(
      (agr, column) => {
        if (!skipColumns[column.id]) {
          agr.push(column);
        }
        return agr;
      },
      [],
    ),
    [skipColumns, viewer?.role],
  );
  return (
    <Query
      client={client}
      extractData={extractData}
      url={`/changelog?query=${encodeURIComponent(JSON.stringify({
        where: whereConditions || undefined,
        sort: {
          updatedAt: -1,
        },
        limit: pageSize,
        skip: (pageCurrent - 1) * pageSize,
        select: {
          _id: true,
          action: true,
          author: true,
          target: true,
          createdAt: true,
          updatedAt: true,
          'snapshot._id': true,
          'steps._id': true,
        },
        populate: {
          'author.subject': {
            name: true,
            role: true,
          },
          'target.subject': {
            name: true,
            type: true,
          },
        },
      }))}`}
    >
      {({ data, loading }) => ( // eslint-disable-line no-unused-vars
        <Table
          bordered
          rowKey="_id"
          size="small"
          columns={columns}
          expandedRowKeys={expandedRowKeys}
          // eslint-disable-next-line no-unused-vars, max-len
          expandedRowRender={(record, index) => (
            <Query
              client={client}
              extractData={extractData}
              url={`/changelog/${record._id}/?query=${JSON.stringify({
                select: {
                  steps: true,
                  snapshot: record.action === 'DELETE' ? true : undefined,
                },
              })}`}
            >
              {({ data: stepData, loading: stepLoading }) => { // eslint-disable-line no-unused-vars
                if (!stepData) {
                  return null;
                }
                record = {
                  ...record,
                  steps: stepData.steps || [],
                  snapshot: stepData.snapshot,
                };
                return (
                  <ChangeLogTableExpandWrapper>
                    <Timeline>
                      {
                        (record.steps || []).map((step) => {
                          const stepMoment = moment(step.createdAt);
                          return (
                            <Timeline.Item key={step._id}>
                              <p>
                                <em style={{ opacity: 0.5 }}>
                                  {`At ${
                                    stepMoment.format('HH:MM')
                                  } (${
                                    stepMoment.fromNow()
                                  })`}
                                </em>
                              </p>
                              <>
                                {
                                  step.payload
                                  ? (
                                        (
                                          // eslint-disable-next-line max-len
                                          record.action === Types.CHANGE_LOG_ACTION_CONST.PATCH
                                          && step.payload.diff
                                        )
                                      ? (
                                          <PayloadDiffCached
                                            diff={step.payload.diff}
                                          />
                                        )
                                      : (
                                          // eslint-disable-next-line max-len
                                          record.action === Types.CHANGE_LOG_ACTION_CONST.DELETE
                                          && step.payload.deleted
                                        )
                                      ? (
                                          <CodeBlock json>
                                            {step.payload}
                                          </CodeBlock>
                                        )
                                      : (
                                          <CodeBlock json>
                                            {step.payload}
                                          </CodeBlock>
                                        )
                                    )
                                  : (
                                      // eslint-disable-next-line max-len
                                      [
                                        Types.CHANGE_LOG_ACTION_CONST.CREATE,
                                        Types.CHANGE_LOG_ACTION_CONST.DELETE,
                                      ].includes(record.action)
                                      ? (
                                          <CodeBlock json>
                                            {record.snapshot}
                                          </CodeBlock>
                                        )
                                      : (
                                          <InlineCodeBlock>
                                            N/A
                                          </InlineCodeBlock>
                                        )
                                    )
                                }
                              </>
                            </Timeline.Item>
                          );
                        })
                      }
                    </Timeline>
                  </ChangeLogTableExpandWrapper>
                );
              }}
            </Query>
          )}
          onExpandedRowsChange={(newExpandedRowKeys) => {
            setExpandedRowKeys(newExpandedRowKeys);
          }}
          pagination={{
            current: pageCurrent,
            pageSize,
            pageSizeOptions,
            showSizeChanger: true,
            size: 'small',
            total: data?.count,
            onChange: (newPageCurrent, newPageSize) => {
              setPageCurrent(parseInt(newPageCurrent, 10));
              setPageSize(parseInt(newPageSize, 10));
            },
            onShowSizeChange: (newPageSize) => {
              setPageSize(parseInt(newPageSize, 10))
            },
          }}
          locale={{
            emptyText: 'No records found',
          }}
          dataSource={data?.data || []}
        />
      )}
    </Query>
  )
}

ChangeLogTable.defaultProps = {
  whereConditions: {},
  extractData: response => response?.data || null,
  skipColumns: {},
};

Admin.addToLibrary(
  'FieldChangeLog',
  ({
    changeLogConfig: {
      getWhereConditions = () => ({}),
      ...changeLogConfig
    } = {},
    skip,
    ...config
  } = {}) => FieldReact.create({
    label: null,
    renderContent: (props) => (
      <ChangeLogTable
        {...props}
        {...changeLogConfig}
        whereConditions={getWhereConditions(props)}
      />
    ),
    virtual: [() => true],
    skip: [
      ...(skip || []),
    ],
    ...config,
  }),
);

export function createChangeLogSection({
  title = 'ChangeLog',
  subtitle = 'Detailed log tracking all related activity.',
  name = 'showChangeLog',
  skip,
  changeLogConfig,
  ...commonConfig
} = {}) {
  const commonSkip = [
    tests.activityIsNotEdit,
    tests.viewerIsNotAdmin,
    // tests.userIsAdminWithThehiveRsEmail,
  ];
  return [
    ['FieldTitleToggle', {
      title,
      subtitle,
      name,
      initialValue: false,
      virtual: [() => true],
      skip: [
        ...(skip || []),
        ...commonSkip,
      ],
      ...commonConfig,
    }],
    ['FieldChangeLog', {
      changeLogConfig,
      skip: [
        ...(skip || []),
        ...commonSkip,
        ['condition.fieldIsNotTrue', name],
      ],
      ...commonConfig,
    }],
  ];
}
